import { FeatureAccessContext } from "@/context/featureAccess";
import { useInteractionAdditionalContextApi } from "@/v2/entities/interaction-additional-context/InteractionAdditionalContext.api";
import { useQuackchatApi } from "@/v2/entities/quackchat/Quackchat.api";
import {
  QuackChatSystemMessageType,
  quackChatSystemMessageTypesMap,
} from "@/v2/entities/quackchat/Quackchat.model";
import { useContext, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "sonner";
import { MessageFilter } from "./SystemMessage.model";
import { FormField } from "@/v2/components/form/form-schema";
import { useGetTopics } from "@/api/useGetTopics";

export function useGetQuackChatFieldOptions(type?: QuackChatSystemMessageType) {
  const { getQuackChatFieldOptions } = useInteractionAdditionalContextApi();
  const { data: topics } = useGetTopics();
  const { selectedWorkspace } = useContext(FeatureAccessContext);

  const { data } = useQuery<FormField[]>({
    queryKey: [
      "interaction-additional-context",
      "topics",
      "options",
      "quackchat",
    ],
    queryFn: getQuackChatFieldOptions,
  });

  return useMemo(() => {
    const options = [...(data ?? [])];

    if (topics && topics.length > 0 && type !== "WELCOME") {
      options.push({
        id: "topic",
        label: "Topic",
        kind: "select",
        options: topics.map((t: any) => ({
          label: t.topic,
          value: String(t.topic),
        })),
      } as FormField);
    }

    if (selectedWorkspace?.includes("yotpo")) {
      options.push({
        id: "total_acv",
        label: "Total ACV",
        kind: "number",
      } as FormField);
    }

    return options;
  }, [data, topics, type, selectedWorkspace]);
}

export function useQuackChatSystemMessage(
  selectedMessageType: QuackChatSystemMessageType | undefined,
  simulatorId: string | undefined,
  onError?: () => void
) {
  const queryClient = useQueryClient();
  const { selectedWorkspace } = useContext(FeatureAccessContext);
  const quackchatApi = useQuackchatApi();

  const {
    data: systemMessageWithConditions,
    refetch: refetchSystemMessageConditions,
  } = useQuery({
    queryKey: [
      "quackchat-system-message-conditions",
      selectedWorkspace,
      selectedMessageType,
    ],
    queryFn: () =>
      quackchatApi.getAllSystemMessageConditions({
        tenant: selectedWorkspace,
        simulatorId,
        type: selectedMessageType ?? "WELCOME",
      }),
    enabled: selectedMessageType != null,
  });

  const {
    data: systemMessages,
    isLoading: loadingSystemMessages,
    refetch: refetchSystemMessages,
  } = useQuery({
    queryKey: ["quackchat-system-messages", selectedWorkspace],
    queryFn: () =>
      quackchatApi.getAllSystemMessages({
        tenant: selectedWorkspace,
        simulatorId,
      }),
    onError,
  });

  const { mutate: toggleSystemMessage } = useMutation({
    mutationKey: ["quackchat-system-message", selectedWorkspace],
    mutationFn: ({
      type,
      enabled,
    }: {
      type: QuackChatSystemMessageType;
      enabled: boolean;
    }) =>
      quackchatApi.toggleSystemMessage({
        type,
        enabled,
        tenant: selectedWorkspace,
        simulatorId,
      }),
    onSuccess: ({ enabled }, { type }) => {
      refetchSystemMessages();
      queryClient.invalidateQueries([
        "quackchat-system-message",
        type,
        selectedWorkspace,
      ]);
      queryClient.invalidateQueries([
        "quackchat-system-message-conditions",
        selectedWorkspace,
        type,
      ]);
      const title = quackChatSystemMessageTypesMap[type].title;
      const action = enabled ? "enabled" : "disabled";

      toast.success(`${title} ${action}`);
    },
    onError: (_, { type, enabled }) => {
      const title = quackChatSystemMessageTypesMap[type].title;
      const action = enabled ? "enable" : "disable";

      toast.error(`Failed to ${action} ${title}`);
    },
  });

  const { mutate: overrideSystemMessage, isLoading: isSavingFilter } =
    useMutation({
      mutationKey: ["quackchat-system-message-override", selectedWorkspace],
      mutationFn: ({
        type,
        message,
        filterRules,
        id,
      }: {
        type: QuackChatSystemMessageType;
        message: string;
        filterRules: MessageFilter[];
        id?: string;
      }) =>
        quackchatApi.overrideSystemMessage({
          type,
          message,
          conditions: filterRules,
          tenant: selectedWorkspace,
          simulatorId,
          id,
        }),
      onSuccess: () => {
        refetchSystemMessages();
        queryClient.invalidateQueries([
          "quackchat-system-message",
          selectedWorkspace,
        ]);
      },
      onError: () => {
        toast.error("Failed to save filter rule");
      },
    });

  return {
    systemMessageWithConditions,
    refetchSystemMessageConditions,
    systemMessages,
    loadingSystemMessages,
    refetchSystemMessages,
    toggleSystemMessage,
    overrideSystemMessage,
    isSavingFilter,
  };
}
